import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import * as XLSX from 'xlsx';
import { logDeletion } from './auditLog';
import { formatToDDMonYYYY } from './dateUtils';
import { normalizeAssets, normalizeCallLogs } from './DataNormalizer';

const CRITICAL_EQUIPMENT_CONFIG_KEY = 'bme-critical-equipment-config';
const DEFAULT_CRITICAL_EQUIPMENT_LIST = [
  { id: 1, name: 'ABG Machine', quantity: 1, matchPattern: ['ABG'] },
  { id: 2, name: 'Immunoassay Analyser', quantity: 1, matchPattern: ['Immunoassay', 'Immuno Assay'] },
  { id: 3, name: 'Anesthesia Machine', quantity: 5, matchPattern: ['Anesthesia'] },
  { id: 4, name: 'Blood Cell Counter', quantity: 1, matchPattern: ['Blood Cell Counter'] },
  { id: 5, name: 'C Arm Machine', quantity: 1, matchPattern: ['C-ARM', 'C ARM'] },
  { id: 6, name: 'CT Scanner', quantity: 1, matchPattern: ['CT Scan'] },
  { id: 7, name: 'Defibrillator', quantity: 17, matchPattern: ['Defibrillator'] },
  { id: 8, name: 'Dialysis Machine', quantity: 6, matchPattern: ['HemoDialysis', 'Dialysis'] },
  { id: 9, name: 'Echo Machine', quantity: 1, matchPattern: ['ECHO'] },
  { id: 10, name: 'ETO Sterilizer', quantity: 1, matchPattern: ['ETO Sterilizer'] },
  { id: 11, name: 'Laproscopy System', quantity: 1, matchPattern: ['Laproscopy'] },
  { id: 12, name: 'Patient Monitor', quantity: 31, matchPattern: ['Patient Monitor'] },
  { id: 13, name: 'Ecg Machine', quantity: 12, matchPattern: ['ECG Machine', 'ECG'] },
  { id: 14, name: 'Drychemistry Analyser', quantity: 1, matchPattern: ['Drychemistry', 'Dry chemistry'] },
  { id: 15, name: 'X ray Machine', quantity: 1, matchPattern: ['X-Ray', 'X ray'] },
  { id: 16, name: 'Mammography', quantity: 1, matchPattern: ['Mammography'] },
  { id: 17, name: 'Hematology machine', quantity: 1, matchPattern: ['Hematology'] },
  { id: 18, name: 'USG Machine', quantity: 2, matchPattern: ['USG'] },
  { id: 19, name: 'Ventilator', quantity: 12, matchPattern: ['Ventilator'] },
  { id: 20, name: 'Cath Lab', quantity: 1, matchPattern: ['Cathlab', 'Cath Lab'] },
  { id: 21, name: 'IABP Machine', quantity: 1, matchPattern: ['IABP'] },
  { id: 22, name: 'HLM', quantity: 1, matchPattern: ['Heart lung machine', 'HLM'] }
];

const Dashboard = ({ currentUser }) => {
  const [assets, setAssets] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [teamActivity, setTeamActivity] = useState([]);
  // State for critical equipment downtime data
  const [criticalEquipmentData, setCriticalEquipmentData] = useState([]);
  const [monthlyAverageMinutes, setMonthlyAverageMinutes] = useState(0);
  const [monthlyAverageHours, setMonthlyAverageHours] = useState(0);
  // State for major breakdowns (> 48 hours)
  const [majorBreakdowns, setMajorBreakdowns] = useState([]);
  // State for spare part replacements
  const [spareReplacements, setSpareReplacements] = useState([]);
  // State for messages
  const [message, setMessage] = useState({ text: '', type: '' });
  // State for new dashboard metrics
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalComplaints: 0,
    breakdownCalls: 0,
    totalTrainingSessions: 0,
    majorBreakdownCalls: 0,
    spareUsed: 0,
    pmThisMonthDue: 0,
    pmNextMonthDue: 0,
    calThisMonthDue: 0,
    calNextMonthDue: 0,
    totalSavings: 0
  });
  const [criticalEquipmentList, setCriticalEquipmentList] = useState(DEFAULT_CRITICAL_EQUIPMENT_LIST);
  const [showCriticalEquipmentEditor, setShowCriticalEquipmentEditor] = useState(false);
  const [criticalEquipmentInput, setCriticalEquipmentInput] = useState('');

  const sanitizeCriticalEquipmentList = (list = []) => {
    if (!Array.isArray(list)) {
      return [...DEFAULT_CRITICAL_EQUIPMENT_LIST];
    }

    const cleaned = list
      .map((item) => ({
        name: String(item?.name || '').trim(),
        quantity: Number(item?.quantity) > 0 ? Number(item.quantity) : 1,
        matchPattern: Array.isArray(item?.matchPattern)
          ? item.matchPattern.map((pattern) => String(pattern || '').trim()).filter(Boolean)
          : []
      }))
      .filter((item) => item.name.length > 0)
      .map((item, index) => ({
        id: index + 1,
        name: item.name,
        quantity: item.quantity,
        matchPattern: item.matchPattern.length > 0 ? item.matchPattern : [item.name]
      }));

    return cleaned.length > 0 ? cleaned : [...DEFAULT_CRITICAL_EQUIPMENT_LIST];
  };

  const formatCriticalEquipmentInput = (list = []) => {
    return list
      .map((item) => `${item.name}|${item.quantity}|${(item.matchPattern || []).join(',')}`)
      .join('\n');
  };

  const parseCriticalEquipmentInput = (inputText = '') => {
    const rows = String(inputText)
      .split('\n')
      .map((row) => row.trim())
      .filter((row) => row && !row.startsWith('#'));

    const parsed = rows.map((row) => {
      const [nameRaw, qtyRaw, patternsRaw] = row.split('|').map((part) => String(part || '').trim());
      const quantity = Number.parseInt(qtyRaw, 10);
      const matchPattern = patternsRaw
        ? patternsRaw.split(',').map((part) => part.trim()).filter(Boolean)
        : [];

      return {
        name: nameRaw,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        matchPattern
      };
    });

    return sanitizeCriticalEquipmentList(parsed);
  };

  const loadCriticalEquipmentConfig = () => {
    try {
      const raw = sessionStorage.getItem(CRITICAL_EQUIPMENT_CONFIG_KEY);
      if (!raw) {
        const defaults = sanitizeCriticalEquipmentList(DEFAULT_CRITICAL_EQUIPMENT_LIST);
        setCriticalEquipmentList(defaults);
        setCriticalEquipmentInput(formatCriticalEquipmentInput(defaults));
        return;
      }

      const parsed = JSON.parse(raw);
      const sanitized = sanitizeCriticalEquipmentList(parsed);
      setCriticalEquipmentList(sanitized);
      setCriticalEquipmentInput(formatCriticalEquipmentInput(sanitized));
    } catch (_error) {
      const defaults = sanitizeCriticalEquipmentList(DEFAULT_CRITICAL_EQUIPMENT_LIST);
      setCriticalEquipmentList(defaults);
      setCriticalEquipmentInput(formatCriticalEquipmentInput(defaults));
    }
  };

  const saveCriticalEquipmentConfig = () => {
    try {
      const parsed = parseCriticalEquipmentInput(criticalEquipmentInput);
      sessionStorage.setItem(CRITICAL_EQUIPMENT_CONFIG_KEY, JSON.stringify(parsed));
      setCriticalEquipmentList(parsed);
      setShowCriticalEquipmentEditor(false);
      showMessage('Critical equipment list saved for this hospital.', 'success');
    } catch (_error) {
      showMessage('Could not save critical equipment list. Check the list format.', 'error');
    }
  };

  const resetCriticalEquipmentConfig = () => {
    const defaults = sanitizeCriticalEquipmentList(DEFAULT_CRITICAL_EQUIPMENT_LIST);
    sessionStorage.removeItem(CRITICAL_EQUIPMENT_CONFIG_KEY);
    setCriticalEquipmentList(defaults);
    setCriticalEquipmentInput(formatCriticalEquipmentInput(defaults));
    setShowCriticalEquipmentEditor(false);
    showMessage('Critical equipment list reset to default for this hospital.', 'success');
  };

  useEffect(() => {
    loadCriticalEquipmentConfig();
  }, [currentUser?.site_id]);

  useEffect(() => {
    if (Array.isArray(callLogs)) {
      calculateCriticalEquipmentDowntime(callLogs, criticalEquipmentList);
    }
  }, [criticalEquipmentList]);

  useEffect(() => {
    loadData();
    
    // Set up interval for auto-refresh if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadData();
      }, 30000); // Refresh every 30 seconds (reduced from 5s to improve performance)
    }
    
    // Clean up interval on component unmount or when autoRefresh changes
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh]);

  const getLogTextByKeys = (log, keys = []) => {
    for (const key of keys) {
      const value = log?.[key];
      if (typeof value === 'string' && value.trim() !== '') {
        return value.toLowerCase();
      }
    }
    return '';
  };

  const loadData = async () => {
    try {
      let loadedAssets = [];
      let logs = [];
      
      // Try loading assets from API first
      try {
        const assetsResponse = await fetch('/api/assets');
        if (assetsResponse.ok) {
          const assetsPayload = await assetsResponse.json();
          loadedAssets = Array.isArray(assetsPayload)
            ? assetsPayload
            : Array.isArray(assetsPayload?.data)
              ? assetsPayload.data
              : [];
          console.log('=== DASHBOARD DATA LOADING DEBUG ===');
          console.log('✅ API Response OK');
          console.log('📊 Assets from API:', loadedAssets.length, 'assets');
        } else {
          throw new Error('API call failed');
        }
      } catch (error) {
        console.warn('⚠️ API unavailable for assets:', error);
      }
      
      if (!Array.isArray(loadedAssets)) {
        loadedAssets = [];
      }

      loadedAssets = normalizeAssets(loadedAssets);

      if (loadedAssets.length > 0) {
        console.log('📦 First asset structure:', JSON.stringify(loadedAssets[0], null, 2));
        console.log('🔧 Assets with pmDue:', loadedAssets.filter(a => a.pmDue && a.pmDue.trim() !== '').length);
        console.log('📅 Assets with calDue:', loadedAssets.filter(a => a.calDue && a.calDue.trim() !== '').length);
      }
      setAssets(loadedAssets);

      // Try loading call logs from API first
      try {
        const logsResponse = await fetch('/api/call-logs');
        if (logsResponse.ok) {
          const logsPayload = await logsResponse.json();
          logs = Array.isArray(logsPayload)
            ? logsPayload
            : Array.isArray(logsPayload?.data)
              ? logsPayload.data
              : [];
          console.log('✅ Loaded', logs.length, 'call logs from API');
        } else {
          throw new Error('API call failed');
        }
      } catch (error) {
        console.warn('⚠️ API unavailable for call logs:', error);
        logs = [];
      }
      
      if (!Array.isArray(logs)) {
        logs = [];
      }

      logs = normalizeCallLogs(logs);

      setCallLogs(logs);
      
      // Get recent team activity (last 10 logs with user info)
      const recentActivity = [...logs]
        .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
        .slice(0, 10);
      
      // Calculate major breakdowns (> 48 hours)
      const majorBreakdownData = logs
        .filter(log => {
          const resTime = log.resolutionTime || log.timeToResolve || '0:00';
          const timeStr = typeof resTime === 'string' ? resTime : String(resTime);
          const [hoursStr] = timeStr.split(':');
          return parseInt(hoursStr) >= 48;
        })
        .map((log, index) => ({
          id: index + 1,
          description: log.description || log.problemDescription || 'No description',
          equipmentName: log.equipmentName || 'Unknown Equipment',
          downtime: log.resolutionTime || log.timeToResolve || 'Unknown',
          date: log.createdAt || log.date || 'Unknown Date'
        }));
      
      // Calculate spare part replacements
      const spareReplacementLogs = logs.filter(log => {
        const sparesUsed = log.sparesUsed && log.sparesUsed.trim();
        const sparesCost = log.sparesCost && log.sparesCost.toString().trim();
        return sparesUsed && sparesUsed.toLowerCase() !== 'na' && 
               sparesCost && sparesCost.toLowerCase() !== 'na';
      });
      
      const spareReplacementData = spareReplacementLogs.map((log, index) => ({
        id: index + 1,
        department: log.department || 'Unknown Department',
        equipmentName: log.equipmentName || 'Unknown Equipment',
        spareUsed: log.sparesUsed || 'Unknown Spare',
        sparesCost: log.sparesCost || '',
        savings: log.savingsAfterSparesCost || 'NA',
        timeTaken: log.resolutionTime || 'Unknown'
      }));
      
      // Calculate new dashboard metrics
      const breakdownKeywords = ['breakdown', 'break down'];
      const trainingKeywords = ['training', 'train'];
      
      const hasAnyKeyword = (text, keywords) => {
        if (!text) return false;
        return keywords.some((keyword) => text.includes(keyword));
      };
      
      const getLogTextByKeys = (log, keys = []) => {
        for (const key of keys) {
          const value = log?.[key];
          if (typeof value === 'string' && value.trim() !== '') {
            return value.toLowerCase();
          }
        }
        return '';
      };
      
      const breakdownCalls = logs.filter(log => {
        const complaintText = getLogTextByKeys(log, ['natureOfComplaint', 'nature_of_complaint', 'complaint', 'problemDescription']);
        const actionText = getLogTextByKeys(log, ['actionTaken', 'action_taken', 'serviceAction']);
        const statusText = getLogTextByKeys(log, ['callStatus', 'call_status', 'status']);
        return hasAnyKeyword(complaintText, breakdownKeywords) || hasAnyKeyword(actionText, breakdownKeywords) || hasAnyKeyword(statusText, breakdownKeywords);
      });
      
      const totalTrainingSessions = logs.filter(log => {
        const complaintText = getLogTextByKeys(log, ['natureOfComplaint', 'nature_of_complaint', 'complaint', 'problemDescription']);
        const actionText = getLogTextByKeys(log, ['actionTaken', 'action_taken', 'serviceAction']);
        const statusText = getLogTextByKeys(log, ['callStatus', 'call_status', 'status']);
        return hasAnyKeyword(complaintText, trainingKeywords) || hasAnyKeyword(actionText, trainingKeywords) || hasAnyKeyword(statusText, trainingKeywords);
      });
      
      const majorBreakdownCalls = majorBreakdownData.length;
      const spareReplacementCount = spareReplacementData.length;
      
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
      
      const pmThisMonthDue = loadedAssets.filter(asset => {
        const pmDueDate = parseDate(asset.pmDue);
        return pmDueDate && pmDueDate.getMonth() + 1 === currentMonth && pmDueDate.getFullYear() === currentYear;
      });
      
      const pmNextMonthDue = loadedAssets.filter(asset => {
        const pmDueDate = parseDate(asset.pmDue);
        return pmDueDate && pmDueDate.getMonth() + 1 === nextMonth && pmDueDate.getFullYear() === nextYear;
      });
      
      const calThisMonthDue = loadedAssets.filter(asset => {
        const calDueDate = parseDate(asset.calDue);
        return calDueDate && calDueDate.getMonth() + 1 === currentMonth && calDueDate.getFullYear() === currentYear;
      });
      
      const calNextMonthDue = loadedAssets.filter(asset => {
        const calDueDate = parseDate(asset.calDue);
        return calDueDate && calDueDate.getMonth() + 1 === nextMonth && calDueDate.getFullYear() === nextYear;
      });
      
      const spareReplacements = spareReplacementData.reduce((sum, item) => {
        const cost = parseFloat(item.sparesCost) || 0;
        return sum + cost;
      }, 0);
      
      // BATCH ALL STATE UPDATES TOGETHER to prevent flashing
      setAssets(loadedAssets);
      setCallLogs(logs);
      setTeamActivity(recentActivity);
      calculateCriticalEquipmentDowntime(logs, criticalEquipmentList);
      setMajorBreakdowns(majorBreakdownData);
      setSpareReplacements(spareReplacementData);
      setDashboardMetrics({
        totalComplaints: logs.length,
        breakdownCalls: breakdownCalls.length,
        totalTrainingSessions: totalTrainingSessions.length,
        majorBreakdownCalls: majorBreakdownCalls,
        spareUsed: spareReplacementCount,
        pmThisMonthDue: pmThisMonthDue.length,
        pmNextMonthDue: pmNextMonthDue.length,
        calThisMonthDue: calThisMonthDue.length,
        calNextMonthDue: calNextMonthDue.length,
        totalSavings: spareReplacements
      });
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse date helper function - enhanced for DD-Mon-YY format and Excel serial dates
  const parseDate = (dateString) => {
    // Handle null/undefined/empty values
    if (dateString === null || dateString === undefined || dateString === '' || 
        (typeof dateString === 'string' && dateString.trim() === '')) {
      return null;
    }
    
    try {
      // Try standard date parsing first
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date;
      }
      
      // Handle DD-Mon-YY or DD-Mon-YYYY format (e.g., 25-Dec-12 or 25-Dec-2012)
      if (typeof dateString === 'string' && /^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/.test(dateString.trim())) {
        const parts = dateString.trim().split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthAbbr = parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
          const month = monthNames.indexOf(monthAbbr);
          
          if (month === -1) {
            console.warn('Invalid month in date string:', dateString);
            return null;
          }
          
          let year = parseInt(parts[2], 10);
          if (isNaN(year)) {
            console.warn('Invalid year in date string:', dateString);
            return null;
          }
          
          // Handle 2-digit years (assume 20XX if <= 50, 19XX if > 50)
          if (year < 100) {
            year = year <= 50 ? 2000 + year : 1900 + year;
          }
          
          if (isNaN(day) || day <= 0 || day > 31) {
            console.warn('Invalid day in date string:', dateString);
            return null;
          }
          
          const date = new Date(year, month, day);
          // Validate that the date is valid
          if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
            return date;
          } else {
            console.warn('Invalid date (date overflow):', dateString);
            return null;
          }
        }
      }
      
      // Handle Excel serial dates (numbers)
      if (typeof dateString === 'number') {
        // Excel serial date (1900-based system)
        // Subtract 1 because Excel incorrectly treats 1900 as a leap year
        const excelDate = new Date((dateString - 25569) * 86400 * 1000);
        if (!isNaN(excelDate.getTime())) {
          return excelDate;
        }
      }
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
    }
    
    return null;
  };
  
  // Function to calculate new dashboard metrics
  const calculateDashboardMetrics = (logs, assets) => {
    // Total Complaints - All call logs
    const totalComplaints = logs.length;

    const breakdownKeywords = ['breakdown', 'break down'];
    const trainingKeywords = ['training', 'train'];

    const hasAnyKeyword = (text, keywords) => {
      if (!text) return false;
      return keywords.some((keyword) => text.includes(keyword));
    };
    
    // Breakdown Calls - Logs with "Breakdown" in nature of complaint or action taken
    const breakdownCalls = logs.filter(log => {
      const complaintText = getLogTextByKeys(log, ['natureOfComplaint', 'nature_of_complaint', 'complaint', 'problemDescription']);
      const actionText = getLogTextByKeys(log, ['actionTaken', 'action_taken', 'serviceAction']);
      const statusText = getLogTextByKeys(log, ['callStatus', 'call_status', 'status']);
      return hasAnyKeyword(complaintText, breakdownKeywords) || hasAnyKeyword(actionText, breakdownKeywords) || hasAnyKeyword(statusText, breakdownKeywords);
    }).length;
    
    // Total Training Sessions - Logs with "Training" in nature of complaint or action taken
    const totalTrainingSessions = logs.filter(log => {
      const complaintText = getLogTextByKeys(log, ['natureOfComplaint', 'nature_of_complaint', 'complaint', 'problemDescription']);
      const actionText = getLogTextByKeys(log, ['actionTaken', 'action_taken', 'serviceAction']);
      const statusText = getLogTextByKeys(log, ['callStatus', 'call_status', 'status']);
      return hasAnyKeyword(complaintText, trainingKeywords) || hasAnyKeyword(actionText, trainingKeywords) || hasAnyKeyword(statusText, trainingKeywords);
    }).length;
    
    // Major Breakdown Calls - Logs with downtime > 48 hours (already calculated)
    const majorBreakdownCalls = logs.filter(log => {
      if (log.startTime && log.endTime) {
        try {
          const startDate = new Date(`${log.date}T${log.startTime}`);
          const endDate = new Date(`${log.date}T${log.endTime}`);
          
          // Handle case where end time is next day
          if (endDate < startDate) {
            endDate.setDate(endDate.getDate() + 1);
          }
          
          const downtimeMinutes = (endDate - startDate) / (1000 * 60);
          return downtimeMinutes > 2880; // 48 hours in minutes
        } catch (error) {
          console.warn('Error calculating downtime for log:', log.id, error);
        }
      }
      return false;
    }).length;
    
    // Spare Used - Logs with spares used
    const spareUsed = logs.filter(log => 
      log.sparesUsed && log.sparesUsed.trim() !== ''
    ).length;
    
    // Calculate PM and Calibration statistics using same logic as PM Tracker
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    
    console.log('\n=== PM/CAL CALCULATION DEBUG ===');
    console.log('📅 Today:', today.toDateString());
    console.log('📅 Current month:', firstDayOfMonth.toDateString(), 'to', lastDayOfMonth.toDateString());
    console.log('📅 Next month:', firstDayOfNextMonth.toDateString(), 'to', lastDayOfNextMonth.toDateString());
    console.log('📊 Total assets to process:', assets.length);
    
    let pmThisMonthDue = 0;
    let pmNextMonthDue = 0;
    let calThisMonthDue = 0;
    let calNextMonthDue = 0;
    
    let pmDateCount = 0;
    let calDateCount = 0;
    
    assets.forEach((asset, index) => {
      // Process PM dates
      const pmDueDate = parseDate(asset.pmDue);
      
      if (asset.pmDue) {
        pmDateCount++;
        if (index < 3) {
          console.log(`\nAsset ${index + 1} PM Debug:`);
          console.log('  pmDue raw:', asset.pmDue);
          console.log('  pmDue parsed:', pmDueDate);
        }
      }
      
      // PM This Month Due
      if (pmDueDate && pmDueDate >= firstDayOfMonth && pmDueDate <= lastDayOfMonth) {
        pmThisMonthDue++;
        if (index < 3) console.log('  ✅ Counted in THIS month PM');
      }
      
      // PM Next Month Due
      if (pmDueDate && pmDueDate >= firstDayOfNextMonth && pmDueDate <= lastDayOfNextMonth) {
        pmNextMonthDue++;
        if (index < 3) console.log('  ✅ Counted in NEXT month PM');
      }
      
      // Process Calibration dates
      const calDueDate = parseDate(asset.calDue);
      
      if (asset.calDue) {
        calDateCount++;
        if (index < 3) {
          console.log(`\nAsset ${index + 1} CAL Debug:`);
          console.log('  calDue raw:', asset.calDue);
          console.log('  calDue parsed:', calDueDate);
        }
      }
      
      // Calibration This Month Due
      if (calDueDate && calDueDate >= firstDayOfMonth && calDueDate <= lastDayOfMonth) {
        calThisMonthDue++;
        if (index < 3) console.log('  ✅ Counted in THIS month CAL');
      }
      
      // Calibration Next Month Due
      if (calDueDate && calDueDate >= firstDayOfNextMonth && calDueDate <= lastDayOfNextMonth) {
        calNextMonthDue++;
        if (index < 3) console.log('  ✅ Counted in NEXT month CAL');
      }
    });
    
    console.log('\n=== CALCULATION RESULTS ===');
    console.log('🔧 Assets with pmDue field:', pmDateCount);
    console.log('🔧 PM This Month Due:', pmThisMonthDue);
    console.log('🔧 PM Next Month Due:', pmNextMonthDue);
    console.log('📅 Assets with calDue field:', calDateCount);
    console.log('📅 Cal This Month Due:', calThisMonthDue);
    console.log('📅 Cal Next Month Due:', calNextMonthDue);
    console.log('=========================\n');
    
    // Total Savings - Sum of all savings after spares cost
    let totalSavings = 0;
    logs.forEach(log => {
      const rawSavings = log.savingsAfterSparesCost;
      if (!rawSavings || String(rawSavings).toUpperCase() === 'NA') return;

      const sanitized = String(rawSavings).replace(/[^\d.-]/g, '');
      const numericSavings = parseFloat(sanitized);
      if (!isNaN(numericSavings)) {
        totalSavings += numericSavings;
      }
    });
    
    // Update state with calculated metrics
    setDashboardMetrics({
      totalComplaints,
      breakdownCalls,
      totalTrainingSessions,
      majorBreakdownCalls,
      spareUsed,
      pmThisMonthDue,
      pmNextMonthDue,
      calThisMonthDue,
      calNextMonthDue,
      totalSavings
    });
  };

  // Function to calculate critical equipment downtime data
  const calculateCriticalEquipmentDowntime = (logs = [], configuredList = criticalEquipmentList) => {
    const criticalEquipmentList = sanitizeCriticalEquipmentList(configuredList);

    const parseDateTime = (dateStr, timeStr) => {
      try {
        const safeDate = String(dateStr || '').trim();
        const safeTime = String(timeStr || '').trim() || '00:00';

        if (!safeDate) {
          return null;
        }

        if (safeDate.includes('/')) {
          const [month, day, year] = safeDate.split('/');
          const parsed = new Date(`${String(year || '').trim()}-${String(month || '').padStart(2, '0')}-${String(day || '').padStart(2, '0')}T${safeTime}`);
          return isNaN(parsed.getTime()) ? null : parsed;
        }

        const parsed = new Date(`${safeDate}T${safeTime}`);
        return isNaN(parsed.getTime()) ? null : parsed;
      } catch (_error) {
        return null;
      }
    };

    // Calculate downtime for each equipment
    const equipmentDowntimeData = criticalEquipmentList.map(equipment => {
      // Filter logs for this equipment using improved matching
      const equipmentLogs = logs.filter(log => {
        if (!log.equipmentName) return false;
        
        const equipmentName = log.equipmentName.toLowerCase().trim();
        const equipmentMatch = equipment.name.toLowerCase().trim();
        
        // Direct match
        if (equipmentName === equipmentMatch) {
          return true;
        }
        
        // Partial match (equipment name contains our equipment name)
        if (equipmentName.includes(equipmentMatch)) {
          return true;
        }
        
        // Partial match (our equipment name contains equipment name)
        if (equipmentMatch.includes(equipmentName)) {
          return true;
        }
        
        // Check match patterns
        if (equipment.matchPattern) {
          return equipment.matchPattern.some(pattern => {
            const patternLower = pattern.toLowerCase().trim();
            return equipmentName.includes(patternLower) || patternLower.includes(equipmentName);
          });
        }
        
        return false;
      });
      
      // Count number of calls
      const numberOfCalls = equipmentLogs.length;
      
      // Calculate total downtime in minutes
      let totalDowntimeMinutes = 0;
      equipmentLogs.forEach(log => {
        if (log.startTime && log.endTime) {
          try {
            const startDate = parseDateTime(log.date, log.startTime);
            const endDate = parseDateTime(log.date, log.endTime);
            if (!startDate || !endDate) {
              return;
            }
            
            // Handle case where end time is next day (if end time is before start time)
            if (endDate < startDate) {
              endDate.setDate(endDate.getDate() + 1);
            }
            
            // Calculate downtime in minutes
            const downtimeMinutes = (endDate - startDate) / (1000 * 60);
            totalDowntimeMinutes += Math.max(0, Math.round(downtimeMinutes)); // Ensure non-negative values
          } catch (error) {
            console.warn('Error calculating downtime for log:', log.id, error);
          }
        }
      });
      
      return {
        ...equipment,
        numberOfCalls,
        downtimeMinutes: Math.round(totalDowntimeMinutes)
      };
    });
    
    // Show all equipment in the table
    setCriticalEquipmentData(equipmentDowntimeData);
    
    // Calculate monthly averages (only for equipment with calls)
    const equipmentWithCalls = equipmentDowntimeData.filter(equipment => equipment.numberOfCalls > 0);
    const totalDowntimeMinutes = equipmentWithCalls.reduce((sum, equipment) => sum + equipment.downtimeMinutes, 0);
    const avgMinutes = equipmentWithCalls.length > 0 ? totalDowntimeMinutes / equipmentWithCalls.length : 0;
    setMonthlyAverageMinutes(parseFloat(avgMinutes.toFixed(2)));
    setMonthlyAverageHours(parseFloat((avgMinutes / 60).toFixed(3)));
  };

  // Function to calculate major breakdowns (> 48 hours)
  const calculateMajorBreakdowns = (logs) => {
    // Filter logs with downtime > 48 hours (2880 minutes)
    const majorBreakdownLogs = logs.filter(log => {
      if (log.startTime && log.endTime) {
        try {
          const startDate = new Date(`${log.date}T${log.startTime}`);
          const endDate = new Date(`${log.date}T${log.endTime}`);
          
          // Handle case where end time is next day
          if (endDate < startDate) {
            endDate.setDate(endDate.getDate() + 1);
          }
          
          const downtimeMinutes = (endDate - startDate) / (1000 * 60);
          return downtimeMinutes > 2880; // 48 hours in minutes
        } catch (error) {
          console.warn('Error calculating downtime for log:', log.id, error);
        }
      }
      return false;
    });
    
    // Transform data for display
    const majorBreakdownData = majorBreakdownLogs.map((log, index) => ({
      id: index + 1,
      equipmentName: log.equipmentName || 'Unknown Equipment',
      problemDescription: log.natureOfComplaint || log.actionTaken || 'No description available',
      downtimeHours: log.resolutionTime || 'Unknown'
    }));
    
    setMajorBreakdowns(majorBreakdownData);
  };

  // Function to calculate spare part replacements
  const calculateSpareReplacements = (logs) => {
    // Filter logs that have actual spares used and cost data (exclude NA values)
    const spareReplacementLogs = logs.filter(log => {
      const sparesUsed = log.sparesUsed && log.sparesUsed.trim();
      const sparesCost = log.sparesCost && log.sparesCost.toString().trim();
      // Only include if both spareUsed and sparesCost have actual values (not NA, not empty)
      return sparesUsed && sparesUsed.toLowerCase() !== 'na' && 
             sparesCost && sparesCost.toLowerCase() !== 'na';
    });
    
    // Transform data for display
    const spareReplacementData = spareReplacementLogs.map((log, index) => ({
      id: index + 1,
      department: log.department || 'Unknown Department',
      equipmentName: log.equipmentName || 'Unknown Equipment',
      spareUsed: log.sparesUsed || 'Unknown Spare',
      sparesCost: log.sparesCost || '',
      savings: log.savingsAfterSparesCost || 'NA',
      timeTaken: log.resolutionTime || 'Unknown'
    }));
    
    setSpareReplacements(spareReplacementData);
  };

  const refreshData = () => {
    loadData();
  };

  // Server refresh helper kept for backward-compatible button wiring
  const syncLocalStorageToAPI = async () => {
    try {
      await loadData();
      alert('✅ Data refreshed from central server.');
    } catch (error) {
      console.error('❌ Refresh error:', error);
      alert('❌ Error refreshing server data: ' + error.message);
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  // Show message function
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Delete a call log entry
  const deleteCallLog = async (id) => {
    if (window.confirm('Are you sure you want to delete this service request? This action cannot be undone.')) {
      try {
        // Find the call log details before deletion for audit log
        const logToDelete = callLogs.find(log => log.id === id);
        
        const response = await fetch(`/api/call-logs/${id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          // Log the deletion in audit log
          if (logToDelete) {
            logDeletion(
              'CALL_LOG',
              id,
              `Deleted service request for ${logToDelete.equipmentName || 'Unknown Equipment'} (${logToDelete.department || 'No Dept'}) - Complaint: ${logToDelete.natureOfComplaint?.substring(0, 50) || 'N/A'}`,
              currentUser
            );
          }
          
          // Refresh data to update all sections
          refreshData();
          
          showMessage('Service request deleted successfully!', 'success');
        } else {
          showMessage('Failed to delete service request', 'error');
        }
      } catch (error) {
        console.error('Failed to delete call log:', error);
        showMessage('Failed to delete service request', 'error');
      }
    }
  };

  // Calculate statistics
  const totalAssets = assets.length;
  const totalCallLogs = callLogs.length;
  const pendingCalls = callLogs.filter(log => {
    const status = String(log.callStatus || '').toLowerCase();
    return status.includes('pending');
  }).length;
  const resolvedCalls = callLogs.filter(log => {
    const status = String(log.callStatus || '').toLowerCase();
    return status.includes('resolved') || status.includes('complete');
  }).length;

  // Department-wise call distribution
  const getDepartmentStats = () => {
    const deptMap = {};
    callLogs.forEach(log => {
      if (log.department) {
        deptMap[log.department] = (deptMap[log.department] || 0) + 1;
      }
    });
    
    return Object.entries(deptMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Status distribution
  const getStatusStats = () => {
    const statusMap = {};
    callLogs.forEach(log => {
      const status = log.callStatus || 'Unknown';
      statusMap[status] = (statusMap[status] || 0) + 1;
    });
    
    return Object.entries(statusMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };

  // User activity stats
  const getUserStats = () => {
    const userMap = {};
    callLogs.forEach(log => {
      const userName = log.userName || 'Unknown';
      userMap[userName] = (userMap[userName] || 0) + 1;
    });
    
    return Object.entries(userMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Equipment uptime and breakdown analysis
  const getEquipmentStats = () => {
    const equipmentMap = {};
    
    // Initialize equipment map with all assets
    assets.forEach(asset => {
      equipmentMap[asset.assetId] = {
        assetId: asset.assetId,
        equipmentName: asset.equipmentName,
        department: asset.department,
        totalBreakdowns: 0,
        totalTimeDown: 0, // in minutes
        lastBreakdown: null
      };
    });
    
    // Calculate breakdown stats for each asset
    callLogs.forEach(log => {
      if (equipmentMap[log.assetId]) {
        equipmentMap[log.assetId].totalBreakdowns += 1;
        
        // Calculate downtime if we have start and end times
        if (log.startTime && log.endTime) {
          try {
            const startDate = new Date(`${log.date}T${log.startTime}`);
            const endDate = new Date(`${log.date}T${log.endTime}`);
            
            // Handle case where end time is next day
            if (endDate < startDate) {
              endDate.setDate(endDate.getDate() + 1);
            }
            
            const downtimeMinutes = (endDate - startDate) / (1000 * 60);
            equipmentMap[log.assetId].totalTimeDown += downtimeMinutes;
          } catch (error) {
            console.warn('Error calculating downtime for log:', log.id, error);
          }
        }
        
        // Track last breakdown date
        if (!equipmentMap[log.assetId].lastBreakdown || 
            new Date(log.date) > new Date(equipmentMap[log.assetId].lastBreakdown)) {
          equipmentMap[log.assetId].lastBreakdown = log.date;
        }
      }
    });
    
    // Calculate uptime percentage (assuming 24/7 operation)
    const daysInPeriod = 30; // Assume 30-day period for uptime calculation
    const totalMinutesInPeriod = daysInPeriod * 24 * 60;
    
    Object.values(equipmentMap).forEach(equipment => {
      const downtimePercentage = (equipment.totalTimeDown / totalMinutesInPeriod) * 100;
      equipment.uptimePercentage = Math.max(0, 100 - downtimePercentage);
      equipment.downtimePercentage = downtimePercentage;
    });
    
    return Object.values(equipmentMap)
      .filter(equipment => equipment.totalBreakdowns > 0) // Only show equipment with breakdowns
      .sort((a, b) => b.totalBreakdowns - a.totalBreakdowns);
  };

  // Get top breakdown equipment
  const getTopBreakdownEquipment = () => {
    const equipmentStats = getEquipmentStats();
    return equipmentStats.slice(0, 5); // Top 5 equipment with most breakdowns
  };

  // Get equipment with lowest uptime
  const getLowestUptimeEquipment = () => {
    const equipmentStats = getEquipmentStats();
    return equipmentStats
      .filter(equipment => equipment.uptimePercentage < 100) // Only show equipment with some downtime
      .sort((a, b) => a.uptimePercentage - b.uptimePercentage)
      .slice(0, 5); // Top 5 equipment with lowest uptime
  };

  // Get breakdown counts by type
  const getBreakdownByType = () => {
    const typeCounts = {
      BME: 0,
      CMC: 0,
      Rental: 0,
      Warranty: 0
    };
    
    callLogs.forEach(log => {
      if (log.type && typeCounts.hasOwnProperty(log.type)) {
        typeCounts[log.type]++;
      }
    });
    
    return typeCounts;
  };

  // Recent call logs (last 5)
  const recentCallLogs = [...callLogs]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  // Department stats for chart
  const departmentStats = getDepartmentStats();
  const statusStats = getStatusStats();
  const userStats = getUserStats();
  const equipmentStats = getEquipmentStats();
  const topBreakdownEquipment = getTopBreakdownEquipment();
  const lowestUptimeEquipment = getLowestUptimeEquipment();
  const breakdownByType = getBreakdownByType();

  // Get max values for chart scaling
  const maxDeptCount = departmentStats.length > 0 
    ? Math.max(...departmentStats.map(d => d.count)) 
    : 1;
    
  const maxStatusCount = statusStats.length > 0 
    ? Math.max(...statusStats.map(s => s.count)) 
    : 1;
    
  const maxUserCount = userStats.length > 0 
    ? Math.max(...userStats.map(u => u.count)) 
    : 1;
    
  const maxBreakdowns = topBreakdownEquipment.length > 0 
    ? Math.max(...topBreakdownEquipment.map(e => e.totalBreakdowns)) 
    : 1;
    
  const maxUptime = 100;

  // Export call logs to CSV
  const exportCallLogsToCSV = () => {
    try {
      // Define CSV headers with all fields in proper order for export
      const headers = [
        'Call Date', 'Asset ID', 'Serial Number', 'Floor', 'Equipment', 'Department', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map call logs to CSV rows
      const rows = callLogs.map(log => [
        log.date || '',
        log.assetId || '',
        log.serialNo || '',
        log.floor || '',
        log.equipmentName || '',
        log.department || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        log.callEndDate || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      ].join('\r\n');
      
      // Create download link
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `call_logs_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showMessage('Successfully exported call logs to CSV!', 'success');
    } catch (error) {
      console.error('Failed to export call logs:', error);
      showMessage('Failed to export call logs. Please try again.', 'error');
    }
  };

  // Export call logs to Excel format
  const exportCallLogsToExcel = () => {
    try {
      // Define Excel headers with all fields in proper order for export
      const headers = [
        'Call Date', 'Asset ID', 'Serial Number', 'Floor', 'Equipment', 'Department', 'Attendant',
        'Nature Of Complaint', 'Action Taken', 'Call Time', 'End Time', 'Call Attend Time',
        'Response Time', 'Resolution Time (H:MM)', 'Type', 'Att. By', 'Call End Date',
        'Spares Used', 'Spares Cost', 'Remarks', 'Call Status', 'SN.N', 'User Name', 'User Role', 'Created At'
      ];
      
      // Map call logs to rows
      const rows = callLogs.map(log => [
        log.date || '',
        log.assetId || '',
        log.serialNo || '',
        log.floor || '',
        log.equipmentName || '',
        log.department || '',
        log.attendant || '',
        log.natureOfComplaint || '',
        log.actionTaken || '',
        log.startTime || '',
        log.endTime || '',
        log.callAttendTime || '',
        log.responseTime || '',
        log.resolutionTime || '',
        log.type || 'BME',
        log.attendedBy || 'INHOUSE',
        log.callEndDate || '',
        log.sparesUsed || '',
        log.sparesCost || '',
        log.remarks || '',
        log.callStatus || '',
        log.snN || '',
        log.userName || '',
        log.userRole || '',
        log.createdAt || ''
      ]);
      
      // Create worksheet data
      const worksheetData = [headers, ...rows];
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Call Logs');
      
      // Export to Excel file
      XLSX.writeFile(wb, `call_logs_${new Date().toISOString().slice(0, 10)}.xlsx`);
      
      showMessage('Successfully exported call logs to Excel format!', 'success');
    } catch (error) {
      console.error('Failed to export call logs to Excel:', error);
      showMessage('Failed to export call logs to Excel. Please try again.', 'error');
    }
  };

  return (
    <div className="dashboard-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">📊 Dashboard Overview</h2>
          <div className="card-actions">
            <button className="refresh-btn" onClick={syncLocalStorageToAPI} title="Sync localStorage data to API server">🔄 Sync Data</button>
          </div>
        </div>
        
        {currentUser && (
          <div className="user-info-display">
            👤 {currentUser.name} ({currentUser.role})
          </div>
        )}
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}
        
        <div className="export-buttons">
          <button className="btn btn-info" onClick={exportCallLogsToCSV}>
            📤 Export to CSV
          </button>
          <button className="btn btn-success" onClick={exportCallLogsToExcel}>
            📊 Export to Excel
          </button>
        </div>

        {/* New Dashboard Metrics Overview */}
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon">📋</div>
            <div className="stat-info">
              <h3>Total Complaints</h3>
              <div className="stat-value">{dashboardMetrics.totalComplaints}</div>
            </div>
          </div>
          
          <div className="stat-card success">
            <div className="stat-icon">🔧</div>
            <div className="stat-info">
              <h3>Breakdown Calls</h3>
              <div className="stat-value">{dashboardMetrics.breakdownCalls}</div>
            </div>
          </div>
          
          <div className="stat-card warning">
            <div className="stat-icon">🎓</div>
            <div className="stat-info">
              <h3>Training Sessions</h3>
              <div className="stat-value">{dashboardMetrics.totalTrainingSessions}</div>
            </div>
          </div>
          
          <div className="stat-card info">
            <div className="stat-icon">⚠️</div>
            <div className="stat-info">
              <h3>Major Breakdowns</h3>
              <div className="stat-value">{dashboardMetrics.majorBreakdownCalls}</div>
            </div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
            <div className="stat-icon">⚙️</div>
            <div className="stat-info">
              <h3>Spare Used</h3>
              <div className="stat-value">{dashboardMetrics.spareUsed}</div>
            </div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #f97316' }}>
            <div className="stat-icon">🔧</div>
            <div className="stat-info">
              <h3>PM This Month Due</h3>
              <div className="stat-value">{dashboardMetrics.pmThisMonthDue}</div>
            </div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #ec4899' }}>
            <div className="stat-icon">📅</div>
            <div className="stat-info">
              <h3>PM Next Month Due</h3>
              <div className="stat-value">{dashboardMetrics.pmNextMonthDue}</div>
            </div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #06b6d4' }}>
            <div className="stat-icon">⚙️</div>
            <div className="stat-info">
              <h3>Cal This Month Due</h3>
              <div className="stat-value">{dashboardMetrics.calThisMonthDue}</div>
            </div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>Cal Next Month Due</h3>
              <div className="stat-value">{dashboardMetrics.calNextMonthDue}</div>
            </div>
          </div>
          
          <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <h3>Total Savings</h3>
              <div className="stat-value">₹{dashboardMetrics.totalSavings.toLocaleString('en-IN')}</div>
            </div>
          </div>
        </div>

        {/* Critical Equipment Down Time Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">🏥 Critical Equipment Down Time</h2>
            <div className="card-actions">
              <button
                className="refresh-btn"
                onClick={() => setShowCriticalEquipmentEditor((prev) => !prev)}
                title="Edit Critical Equipment List"
              >
                ⚙️
              </button>
              <button className="refresh-btn" onClick={refreshData}>🔄</button>
            </div>
          </div>
          <div className="critical-equipment-summary">
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">Monthly Average (Min)</span>
                <span className="stat-value">{monthlyAverageMinutes}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Monthly Average (Hrs)</span>
                <span className="stat-value">{monthlyAverageHours}</span>
              </div>
            </div>
          </div>

          {showCriticalEquipmentEditor && (
            <div className="critical-equipment-editor">
              <h3>Critical Equipment List (Hospital Specific)</h3>
              <p className="editor-help">
                Use format: <strong>Equipment Name|Quantity|match1,match2</strong> (one row per line)
              </p>
              <textarea
                className="critical-equipment-textarea"
                value={criticalEquipmentInput}
                onChange={(e) => setCriticalEquipmentInput(e.target.value)}
                rows={10}
              />
              <div className="editor-actions">
                <button className="refresh-btn" onClick={saveCriticalEquipmentConfig}>💾 Save</button>
                <button className="refresh-btn" onClick={resetCriticalEquipmentConfig}>↺ Reset Default</button>
                <button
                  className="refresh-btn"
                  onClick={() => {
                    setCriticalEquipmentInput(formatCriticalEquipmentInput(criticalEquipmentList));
                    setShowCriticalEquipmentEditor(false);
                  }}
                >
                  ✖ Close
                </button>
              </div>
            </div>
          )}
          
          <div className="table-container">
            <table className="critical-equipment-table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Equipment Name</th>
                  <th>Quantity</th>
                  <th>No. of Calls</th>
                  <th>Down Time (Min)</th>
                </tr>
              </thead>
              <tbody>
                {criticalEquipmentData.length > 0 ? (
                  criticalEquipmentData.map((equipment) => (
                    <tr key={equipment.id} className={equipment.numberOfCalls === 0 ? 'no-calls' : ''}>
                      <td>{equipment.id}</td>
                      <td>{equipment.name}</td>
                      <td>{equipment.quantity}</td>
                      <td>{equipment.numberOfCalls}</td>
                      <td>{equipment.downtimeMinutes}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">No critical equipment data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Major Breakdown Detail Section (> 48Hr) */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">⚠️ Major Breakdown Detail (&gt; 48Hr)</h2>
            <div className="card-actions">
              <button className="refresh-btn" onClick={refreshData}>🔄</button>
            </div>
          </div>
          <div className="table-container">
            <table className="major-breakdown-table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Equipment Name</th>
                  <th>Problem Description</th>
                  <th>Down Time (Hr)</th>
                </tr>
              </thead>
              <tbody>
                {majorBreakdowns.length > 0 ? (
                  majorBreakdowns.map((breakdown) => (
                    <tr key={breakdown.id}>
                      <td>{breakdown.id}</td>
                      <td>{breakdown.equipmentName}</td>
                      <td>{breakdown.problemDescription}</td>
                      <td>{breakdown.downtimeHours}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">No major breakdowns (&gt; 48Hr) found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Spare Part Replacement Detail Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">⚙️ Spare Part Replacement Detail</h2>
            <div className="card-actions">
              <button className="refresh-btn" onClick={refreshData}>🔄</button>
            </div>
          </div>
          <div className="table-container">
            <table className="spare-replacement-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Equipment Name</th>
                  <th>Spare Used</th>
                  <th>Spares Cost</th>
                  <th>Savings</th>
                  <th>Time taken for spare replacement (HH:MM)</th>
                </tr>
              </thead>
              <tbody>
                {spareReplacements.length > 0 ? (
                  spareReplacements.map((replacement) => (
                    <tr key={replacement.id}>
                      <td>{replacement.department}</td>
                      <td>{replacement.equipmentName}</td>
                      <td>{replacement.spareUsed}</td>
                      <td>{replacement.sparesCost}</td>
                      <td>{replacement.savings || 'NA'}</td>
                      <td>{replacement.timeTaken}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="no-data">No spare part replacements found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Equipment Analysis Section */}
        <div className="charts-grid">
          {/* Equipment with Most Breakdowns */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">🔧 Equipment with Most Breakdowns</h2>
              <div className="card-actions">
                <button className="refresh-btn" onClick={refreshData}>🔄</button>
              </div>
            </div>
            <div className="chart-container">
              {topBreakdownEquipment.length > 0 ? (
                topBreakdownEquipment.map(equipment => (
                  <div key={equipment.assetId} className="chart-item">
                    <div className="chart-label">
                      <span className="equipment-name">{equipment.equipmentName}</span>
                      <span className="count">{equipment.totalBreakdowns} breakdowns</span>
                    </div>
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar"
                        style={{ 
                          width: `${(equipment.totalBreakdowns / maxBreakdowns) * 100}%`,
                          background: 'linear-gradient(90deg, #ef4444, #dc2626)'
                        }}
                      ></div>
                    </div>
                    <div className="equipment-details">
                      <small>Asset ID: {equipment.assetId} | Department: {equipment.department}</small>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">No breakdown data available</p>
              )}
            </div>
          </div>

          {/* Equipment Uptime Analysis */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">📈 Equipment Uptime Analysis</h2>
              <div className="card-actions">
                <button className="refresh-btn" onClick={refreshData}>🔄</button>
              </div>
            </div>
            <div className="chart-container">
              {lowestUptimeEquipment.length > 0 ? (
                lowestUptimeEquipment.map(equipment => (
                  <div key={equipment.assetId} className="chart-item">
                    <div className="chart-label">
                      <span className="equipment-name">{equipment.equipmentName}</span>
                      <span className="count">{equipment.uptimePercentage.toFixed(1)}% uptime</span>
                    </div>
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar"
                        style={{ 
                          width: `${equipment.uptimePercentage}%`,
                          background: 'linear-gradient(90deg, #94a3b8, #64748b)'
                        }}
                      ></div>
                    </div>
                    <div className="equipment-details">
                      <small>Downtime: {equipment.totalTimeDown.toFixed(1)} minutes | Breakdowns: {equipment.totalBreakdowns}</small>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">No uptime data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-grid">
          {/* Department-wise Call Distribution */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">CallCheck by Department</h2>
              <div className="card-actions">
                <button className="refresh-btn" onClick={refreshData}>🔄</button>
              </div>
            </div>
            <div className="chart-container">
              {departmentStats.length > 0 ? (
                departmentStats.map(dept => (
                  <div key={dept.name} className="chart-item">
                    <div className="chart-label">
                      <span className="dept-name">{dept.name}</span>
                      <span className="count">{dept.count} calls</span>
                    </div>
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar"
                        style={{ 
                          width: `${(dept.count / maxDeptCount) * 100}%`,
                          background: 'linear-gradient(90deg, #4cc9f0, #4361ee)'
                        }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">No department data available</p>
              )}
            </div>
          </div>

          {/* Call Status Distribution */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">CallCheck Status Distribution</h2>
              <div className="card-actions">
                <button className="refresh-btn" onClick={refreshData}>🔄</button>
              </div>
            </div>
            <div className="chart-container">
              {statusStats.length > 0 ? (
                statusStats.map(status => {
                  // Determine color based on status
                  let bgColor = 'linear-gradient(90deg, #94a3b8, #64748b)';
                  if (status.name.toLowerCase().includes('pending')) {
                    bgColor = 'linear-gradient(90deg, #fbbf24, #f59e0b)';
                  } else if (status.name.toLowerCase().includes('progress')) {
                    bgColor = 'linear-gradient(90deg, #3b82f6, #2563eb)';
                  } else if (status.name.toLowerCase().includes('resolved')) {
                    bgColor = 'linear-gradient(90deg, #10b981, #059669)';
                  }
                  
                  return (
                    <div key={status.name} className="chart-item">
                      <div className="chart-label">
                        <span className="status-name">{status.name}</span>
                        <span className="count">{status.count} calls</span>
                      </div>
                      <div className="chart-bar-container">
                        <div 
                          className="chart-bar"
                          style={{ 
                            width: `${(status.count / maxStatusCount) * 100}%`,
                            background: bgColor
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="no-data">No status data available</p>
              )}
            </div>
          </div>

          {/* User Activity Chart */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">CallCheck by Team Member</h2>
              <div className="card-actions">
                <button className="refresh-btn" onClick={refreshData}>🔄</button>
              </div>
            </div>
            <div className="chart-container">
              {userStats.length > 0 ? (
                userStats.map(user => (
                  <div key={user.name} className="chart-item">
                    <div className="chart-label">
                      <span className="user-name">{user.name}</span>
                      <span className="count">{user.count} calls</span>
                    </div>
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar"
                        style={{ 
                          width: `${(user.count / maxUserCount) * 100}%`,
                          background: 'linear-gradient(90deg, #4cc9f0, #4361ee)'
                        }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">No user data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Team Activity */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Recent Team Activity</h2>
            <div className="card-actions">
              <button className="refresh-btn" onClick={refreshData}>🔄</button>
            </div>
          </div>
          <div className="table-container">
            {teamActivity.length > 0 ? (
              <table className="recent-logs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>User</th>
                    <th>Asset ID</th>
                    <th>Floor</th> {/* Add Floor column */}
                    <th>Department</th>
                    <th>Complaint</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {teamActivity.map(log => (
                    <tr key={log.id}>
                      <td>{formatToDDMonYYYY(log.date)}</td>
                      <td>{log.startTime || 'N/A'}</td>
                      <td>
                        <span className="user-badge-small">
                          {log.userName} ({log.userRole})
                        </span>
                      </td>
                      <td>{log.assetId}</td>
                      <td>{log.floor}</td> {/* Add floor column */}
                      <td>{log.department}</td>
                      <td className="complaint-cell">
                        {log.natureOfComplaint.length > 30 
                          ? log.natureOfComplaint.substring(0, 30) + '...' 
                          : log.natureOfComplaint}
                      </td>
                      <td>
                        <span className={`status-badge status-${log.callStatus.toLowerCase().replace(/\s+/g, '-').replace(/\(|\)/g, '')}`}>
                          {log.callStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No team activity found</p>
            )}
          </div>
        </div>

        {/* Recent Call Logs */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Your Recent Service Requests</h2>
            <div className="card-actions">
              <button className="refresh-btn" onClick={refreshData}>🔄</button>
            </div>
          </div>
          <div className="table-container">
            {recentCallLogs.length > 0 ? (
              <table className="recent-logs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Call Time</th>
                    <th>End Time</th>
                    <th>Asset ID</th>
                    <th>Floor</th> {/* Add Floor column */}
                    <th>Department</th>
                    <th>Complaint</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCallLogs.map(log => (
                    <tr key={log.id}>
                      <td>{formatToDDMonYYYY(log.date)}</td>
                      <td>{log.startTime || 'N/A'}</td>
                      <td>{log.endTime || 'N/A'}</td>
                      <td>{log.assetId}</td>
                      <td>{log.floor}</td> {/* Add floor column */}
                      <td>{log.department}</td>
                      <td className="complaint-cell">
                        {log.natureOfComplaint.length > 30 
                          ? log.natureOfComplaint.substring(0, 30) + '...' 
                          : log.natureOfComplaint}
                      </td>
                      <td>
                        <span className={`status-badge status-${log.callStatus.toLowerCase().replace(/\s+/g, '-').replace(/\(|\)/g, '')}`}>
                          {log.callStatus}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="delete-btn"
                          onClick={(e) => { e.stopPropagation(); deleteCallLog(log.id); }}
                          title="Delete this service request"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No service requests found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
